// monitoring-ui/components/AIActivityTab.tsx
import React, { useState } from 'react';
import { MetricCard, badgeStyle, Mono, SectionLabel } from './MonitoringUi';
import type { AIAction } from '../Model/Monitoring';

// ─── Feature request modal ────────────────────────────────────────────────────

const FEATURES = [
  { id: 'rightsizing',    icon: '💰', label: 'AI Auto-Rightsizing',              desc: 'Automatically resize over/under-provisioned instances' },
  { id: 'security',      icon: '🛡️', label: 'Security Threat Auto-blocking',     desc: 'Detect brute force, DDoS, credential stuffing and block IPs' },
  { id: 'rollback',      icon: '🔄', label: 'Auto-rollback on Error Spike',       desc: 'Instantly roll back bad deploys when error rate crosses threshold' },
  { id: 'ssl',           icon: '🔐', label: 'SSL Cert Auto-renewal',              desc: 'Never get caught with an expired cert again' },
  { id: 'logs',          icon: '📋', label: 'Log Aggregation & Search',           desc: 'Search across all deployment logs in one place' },
  { id: 'alerts',        icon: '📱', label: 'Slack / PagerDuty Alert Routing',   desc: 'Send critical alerts to the right channel or on-call person' },
  { id: 'db',            icon: '🗄️', label: 'Database Query Insights',           desc: 'Detect N+1 queries, slow queries, and pool exhaustion' },
  { id: 'cost_anomaly',  icon: '📈', label: 'Cost Anomaly Detection',            desc: 'Alert when spend spikes unexpectedly vs baseline' },
  { id: 'zombie',        icon: '🧹', label: 'Zombie Instance Cleanup',           desc: 'Find and kill idle instances burning money silently' },
  { id: 'thresholds',    icon: '⚙️', label: 'Custom Alert Thresholds',           desc: 'Set your own CPU / memory / latency alert rules per service' },
];

const FeatureRequestModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const toggle = (id: string) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const handleSubmit = () => {
    const body = [
      'Hi QixDeploy team,',
      '',
      "I'd love to see these features prioritized:",
      ...Array.from(selected).map(id => {
        const f = FEATURES.find(f => f.id === id);
        return f ? `  • ${f.icon} ${f.label}` : '';
      }),
      '',
      'Please let me know when these are ready!',
    ].join('\n');
    const subject = encodeURIComponent('Feature request: AI Activity & Alerts');
    window.location.href = `mailto:product@qixdeploy.com?subject=${subject}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(5,5,8,.75)' }}
      onClick={onClose}>
      <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '14px', padding: '24px', width: '480px', maxWidth: '92vw', maxHeight: '80vh', overflowY: 'auto', fontFamily: "'Inter',sans-serif" }}
        onClick={e => e.stopPropagation()}>

        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '4px' }}>
          Which features matter most to you?
        </div>
        <div style={{ fontSize: '11.5px', color: '#52525f', marginBottom: '18px' }}>
          We use these votes to prioritize what we build next. Select everything you care about.
        </div>

        {!submitted ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
              {FEATURES.map(f => (
                <label key={f.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', background: selected.has(f.id) ? 'rgba(99,102,241,.08)' : '#17171e', border: `1px solid ${selected.has(f.id) ? 'rgba(99,102,241,.3)' : '#26262f'}`, borderRadius: '8px', cursor: 'pointer', transition: 'all .15s' }}>
                  <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggle(f.id)}
                    style={{ marginTop: '2px', accentColor: '#6366f1', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '12.5px', fontWeight: 500, color: '#eeeef5', marginBottom: '2px' }}>
                      {f.icon} {f.label}
                    </div>
                    <div style={{ fontSize: '10.5px', color: '#52525f' }}>{f.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer', border: '1px solid #32323d', background: '#17171e', color: '#eeeef5' }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={selected.size === 0}
                style={{ padding: '8px 18px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 600, cursor: selected.size === 0 ? 'not-allowed' : 'pointer', border: 'none', background: selected.size === 0 ? '#3d3f6b' : '#6366f1', color: '#fff', opacity: selected.size === 0 ? 0.6 : 1 }}>
                📧 Send {selected.size > 0 ? `(${selected.size} selected)` : ''} →
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '28px', marginBottom: '10px' }}>🙏</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#eeeef5', marginBottom: '6px' }}>Thanks for your input!</div>
            <div style={{ fontSize: '12px', color: '#8e8ea8', marginBottom: '16px' }}>Your email client should open with the details pre-filled.</div>
            <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: '6px', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer', border: 'none', background: '#6366f1', color: '#fff' }}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Dummy data — every scenario is a real infra pain point ──────────────────

const DUMMY_ACTIONS: AIAction[] = [
  // ── PENDING ──
  {
    id: '1', type: 'resize_pending', status: 'pending',
    title: '🧠 OOM kill detected — resize api-prod-3: 1GB → 2GB RAM',
    description: 'api-prod-3 was OOM-killed 4× in the last 6h (02:14, 03:47, 04:52, 05:31). Each restart takes ~40s and drops in-flight requests. Pattern matches a memory leak in v2.2.x — recommend resize now, investigate leak in parallel. Cost impact: +$8.40/mo. Zero-downtime rolling restart.',
    deployment_id: 'dep_abc1', app_name: 'api-prod-3', repo_name: 'api-service', branch: 'main', timestamp: new Date(Date.now() - 14 * 60000).toISOString(), cost_delta: 8.40,
  },
  {
    id: '2', type: 'security_pending', status: 'pending',
    title: '🛡️ Brute force SSH — 2,847 login attempts from 34 IPs in 90 min',
    description: 'Coordinated credential stuffing attack detected across api-prod-1, api-prod-3, and worker-queue. Source IPs span 12 countries, rotating every ~40 attempts to evade basic fail2ban. None successful so far. Recommend: auto-block /32s, rate-limit SSH to known CIDR, enforce key-only auth. Action can be applied without downtime.',
    deployment_id: 'dep_sec1', app_name: 'api-prod-1', repo_name: 'api-service', branch: 'main', timestamp: new Date(Date.now() - 22 * 60000).toISOString(), cost_delta: null,
  },
  {
    id: '3', type: 'downsize_pending', status: 'pending',
    title: '💰 Zombie instance — ml-inference t3.xlarge idle 9 days ($67/mo wasted)',
    description: 'ml-inference-staging has had avg CPU 1.8% and memory 4.2% for 9 consecutive days. No deployments or jobs have targeted it since May 16. Instance costs $67.20/mo. Recommend: terminate and snapshot. Can be re-created from snapshot in <3 min if needed.',
    deployment_id: 'dep_ml2', app_name: 'ml-inference-staging', repo_name: 'ml-pipeline', branch: 'staging', timestamp: new Date(Date.now() - 55 * 60000).toISOString(), cost_delta: -67.20,
  },
  {
    id: '4', type: 'ssl_pending', status: 'pending',
    title: '🔐 SSL cert expires in 8 days — api-prod-1.yourdomain.com',
    description: 'ACM cert for api-prod-1.yourdomain.com expires Jun 30 2025. Auto-renewal failed silently 3 weeks ago due to DNS validation timing out (likely a dangling CNAME). If not renewed, browsers will show "Your connection is not private" — effectively taking the service offline for end-users. Fix: re-trigger DNS validation. ETA 4 min.',
    deployment_id: 'dep_jkl5', app_name: 'api-prod-1', repo_name: 'api-service', branch: 'main', timestamp: new Date(Date.now() - 120 * 60000).toISOString(), cost_delta: null,
  },
  {
    id: '5', type: 'rightsizing_pending', status: 'pending',
    title: '📦 Rightsizing — staging-env t3.large → t3.small (saves $38/mo)',
    description: '14-day average: CPU 6.2%, memory 21.4%. t3.large is significantly over-provisioned for this workload. Downsize to t3.small — still leaves 60% CPU headroom and 40% memory headroom at observed peak. Non-prod environment, zero risk. Saves $38.40/mo ($460/yr).',
    deployment_id: 'dep_xyz2', app_name: 'staging-env', repo_name: 'api-service', branch: 'staging', timestamp: new Date(Date.now() - 3 * 3600000).toISOString(), cost_delta: -38.40,
  },

  // ── APPLIED ──
  {
    id: '6', type: 'auto_scale', status: 'applied',
    title: '⚡ Auto-scaled web-frontend 2 → 8 instances during traffic spike (+340% req/min)',
    description: 'Traffic spiked from 420 → 1,848 req/min at 14:22. Auto-scaler triggered at 78% CPU. Scale-out completed in 42s before any requests were dropped. P99 latency stayed under 180ms throughout. Scale-in started 22 min later after traffic normalized. Without auto-scaling this would have caused a full outage.',
    deployment_id: 'dep_def3', app_name: 'web-frontend', repo_name: 'web-app', branch: 'main', timestamp: new Date(Date.now() - 4 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '7', type: 'db_pool', status: 'applied',
    title: '🗄️ DB connection pool exhausted — auto-expanded 50 → 120 connections',
    description: 'auth-service hit pool limit at 09:03. Requests queued for 4.2 min causing 503s for ~12% of users. Root cause: batch report job shares the same pool. Auto-expanded pool size and isolated batch to a dedicated read-replica connection. P99 latency returned to baseline in 90s.',
    deployment_id: 'dep_auth1', app_name: 'auth-service', repo_name: 'auth-service', branch: 'main', timestamp: new Date(Date.now() - 6 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '8', type: 'disk_cleanup', status: 'applied',
    title: '🧹 Disk cleanup — worker-queue /var/log at 91%, freed 34 GB',
    description: 'worker-queue disk usage crossed 91% at 05:44. At 95%+ the process crashes and jobs are lost silently. Root cause: log rotation was misconfigured (rotate weekly, compress never). Auto-rotated and compressed logs older than 3 days — freed 34.2 GB. Now at 44%. Recommended fix: enable daily rotation + auto-compress.',
    deployment_id: 'dep_wq1', app_name: 'worker-queue', repo_name: 'worker-service', branch: 'main', timestamp: new Date(Date.now() - 8 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '9', type: 'rollback', status: 'rolled_back',
    title: '🔄 Auto-rolled back api-prod-3 v2.2.1 — error rate hit 9.4% in 3 min',
    description: 'Deploy of v2.2.1 at 16:34 caused error rate to climb from 0.3% → 9.4% within 180s. Auto-rollback triggered at 9% threshold. Rollback completed in 38s. Root cause identified post-mortem: env var DATABASE_POOL_SIZE was missing in the new config — connection exhaustion cascaded into 500s. v2.2.0 is stable.',
    deployment_id: 'dep_abc1', app_name: 'api-prod-3', repo_name: 'api-service', branch: 'main', timestamp: new Date(Date.now() - 10 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '10', type: 'security_blocked', status: 'applied',
    title: '🛡️ DDoS-like burst blocked — 50k req/min from single IP range for 8 min',
    description: '47.*.*.* /16 sent 50,347 req/min between 11:18–11:26 targeting /api/auth/login. Pattern matches credential stuffing via residential proxy network. Rate limiting capped it at 500 req/min per IP, returning 429s. No accounts compromised. 6 IP ranges added to blocklist. Recommend enabling CAPTCHA on /login for IPs outside known user geos.',
    deployment_id: 'dep_auth1', app_name: 'auth-service', repo_name: 'auth-service', branch: 'main', timestamp: new Date(Date.now() - 12 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '11', type: 'query_optimization', status: 'applied',
    title: '🗄️ N+1 query detected — auth-service p99 latency dropped 4.2s → 180ms',
    description: 'Profiling detected a serializer in GET /api/users/:id was issuing 1 query per permission row — avg 148 queries per request at peak load. Query was batched with a single JOIN. No deploy needed — config-level change. p99 latency dropped from 4.2s to 180ms. DB CPU reduced 38%.',
    deployment_id: 'dep_auth1', app_name: 'auth-service', repo_name: 'auth-service', branch: 'main', timestamp: new Date(Date.now() - 18 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '12', type: 'ssl_renew', status: 'applied',
    title: '🔐 SSL cert auto-renewed — app-prod-2.yourdomain.com (was expiring in 3 days)',
    description: 'Cert was 87 days old and approaching its 90-day Let\'s Encrypt expiry. Automated ACM renewal triggered, DNS validation passed in 2m 14s, new cert issued with 90-day validity. Zero downtime. Previous manual renewal was missed — this will now auto-run 30 days before expiry.',
    deployment_id: 'dep_prod2', app_name: 'app-prod-2', repo_name: 'web-app', branch: 'main', timestamp: new Date(Date.now() - 24 * 3600000).toISOString(), cost_delta: null,
  },
  {
    id: '13', type: 'network_anomaly', status: 'applied',
    title: '📡 Unexpected egress spike — 847 GB transferred, traced to runaway log shipper',
    description: 'Network egress spiked to $180 in 6 hours (normal: $8/day). Root cause: a misconfigured Filebeat instance was shipping raw binary files (database dump artifacts) instead of application logs, re-reading on each restart loop. Process killed, config fixed. Estimated wasted cost: $172. Guard added: egress alert at 2× daily baseline.',
    deployment_id: 'dep_wq1', app_name: 'worker-queue', repo_name: 'worker-service', branch: 'main', timestamp: new Date(Date.now() - 28 * 3600000).toISOString(), cost_delta: null,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const actionBadge = (status: AIAction['status']) => {
  if (status === 'applied')     return <span style={{ ...badgeStyle('green'), fontSize: '10px' }}>Applied</span>;
  if (status === 'pending')     return <span style={{ ...badgeStyle('amber'), fontSize: '10px' }}>Awaiting approval</span>;
  if (status === 'rolled_back') return <span style={{ ...badgeStyle('red'),   fontSize: '10px' }}>Rolled back</span>;
  if (status === 'rejected')    return <span style={{ ...badgeStyle('gray'),  fontSize: '10px' }}>Rejected</span>;
  return null;
};

const actionDot = (s: AIAction['status']) =>
  s === 'applied' ? '#22c55e' : s === 'pending' ? '#eab308' : s === 'rolled_back' ? '#f43f5e' : '#818cf8';

const fmtTime = (iso: string) => {
  const d = new Date(iso), now = new Date(), diff = now.getTime() - d.getTime();
  if (diff < 3600000)  return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// ─── Main component ───────────────────────────────────────────────────────────

export const AIActivityTab: React.FC = () => {
  const [exploring,    setExploring]    = useState(false);
  const [showModal,    setShowModal]    = useState(false);

  const actions     = DUMMY_ACTIONS;
  const pending     = actions.filter(a => a.status === 'pending');
  const autoApplied = actions.filter(a => a.status === 'applied').length;
  const rollbacks   = actions.filter(a => a.status === 'rolled_back').length;
  const savedPerMo  = actions.reduce((acc, a) => acc + (a.cost_delta && a.cost_delta < 0 ? Math.abs(a.cost_delta) : 0), 0);

  return (
    <div style={{ position: 'relative' }}>
      {showModal && <FeatureRequestModal onClose={() => setShowModal(false)} />}

      {/* ── Coming soon overlay ───────────────────────────────────────────── */}
      {!exploring && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '40px', pointerEvents: 'none' }}>
          <div style={{ pointerEvents: 'auto', background: 'rgba(9,9,14,0.88)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(99,102,241,.3)', borderRadius: '16px', padding: '28px 32px', maxWidth: '440px', width: '90%', textAlign: 'center', boxShadow: '0 16px 60px rgba(0,0,0,.7)' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤖</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '17px', fontWeight: 700, color: '#eeeef5', marginBottom: '6px' }}>
              AI Activity — Coming Soon
            </div>
            <div style={{ fontSize: '12px', color: '#8e8ea8', lineHeight: 1.7, marginBottom: '20px' }}>
              Auto-detect OOM kills, brute force attacks, idle instances, and bad deploys — then fix them with one click or automatically. What's behind this overlay is a <strong style={{ color: '#c7d2fe' }}>preview of exactly what you'll get</strong>.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => setExploring(true)} style={{ padding: '9px 18px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#6366f1', color: '#fff' }}>
                Explore Preview →
              </button>
              <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(99,102,241,.3)', background: 'transparent', color: '#a5b4fc' }}>
                📧 Request Feature
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview strip when exploring ─────────────────────────────────── */}
      {exploring && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(99,102,241,.07)', border: '1px solid rgba(99,102,241,.18)', borderRadius: '8px', padding: '8px 14px', marginBottom: '14px', fontSize: '11.5px', color: '#a5b4fc' }}>
          <span>👁️ <strong>Preview mode</strong> — sample data only, no real actions are taken</span>
          <button onClick={() => setShowModal(true)} style={{ padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', border: '1px solid rgba(99,102,241,.25)', background: 'transparent', color: '#818cf8' }}>
            📧 Request this feature
          </button>
        </div>
      )}

      {/* ── Content (dimmed behind overlay, full opacity when exploring) ──── */}
      <div style={{ opacity: exploring ? 1 : 0.28, filter: exploring ? 'none' : 'blur(0.8px)', pointerEvents: exploring ? 'auto' : 'none', userSelect: exploring ? 'auto' : 'none', transition: 'opacity .3s, filter .3s' }}>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
          <MetricCard label="Actions today"       value={actions.length}    sub="Across all deployments" />
          <MetricCard label="Auto-applied"        value={autoApplied}       valueColor="#22c55e" sub="No manual intervention" />
          <MetricCard label="Awaiting approval"   value={pending.length}    valueColor="#eab308" sub="Requires your input" />
          <MetricCard label="Savings identified"  value={`$${savedPerMo.toFixed(0)}/mo`} valueColor="#22c55e" sub="From rightsizing + cleanup" />
        </div>

        {/* Pending */}
        {pending.length > 0 && (
          <>
            <SectionLabel>⚠️ Requires your approval</SectionLabel>
            <div style={{ background: 'rgba(234,179,8,.04)', border: '1px solid rgba(234,179,8,.2)', borderRadius: '8px', padding: '14px 16px', marginBottom: '14px' }}>
              {pending.map((action, i) => (
                <div key={action.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: i > 0 ? '12px 0 0' : '0', borderTop: i > 0 ? '1px solid rgba(234,179,8,.12)' : 'none', marginTop: i > 0 ? '12px' : 0 }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#eab308', flexShrink: 0, marginTop: '5px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '12.5px', marginBottom: '5px', color: '#eeeef5' }}>{action.title}</div>
                    <div style={{ fontSize: '11.5px', color: '#8e8ea8', lineHeight: 1.6, marginBottom: '10px' }}>{action.description}</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' as const }}>
                      <button style={{ background: 'rgba(34,197,94,.1)', border: '1px solid rgba(34,197,94,.25)', color: '#22c55e', borderRadius: '6px', padding: '5px 14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Approve ↗</button>
                      <button style={{ background: '#17171e', border: '1px solid #26262f', color: '#8e8ea8', borderRadius: '6px', padding: '5px 14px', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}>Reject</button>
                      <span style={{ fontSize: '10.5px', color: '#52525f' }}>· {fmtTime(action.timestamp)}</span>
                      {action.cost_delta != null && (
                        <span style={{ fontSize: '10.5px', fontWeight: 600, color: action.cost_delta > 0 ? '#f43f5e' : '#22c55e' }}>
                          {action.cost_delta > 0 ? `+$${action.cost_delta}/mo` : `💰 saves $${Math.abs(action.cost_delta)}/mo`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Activity log */}
        <SectionLabel>Activity log</SectionLabel>
        <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '8px', padding: '14px 16px' }}>
          {actions.filter(a => a.status !== 'pending').map((action, i) => (
            <div key={action.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: i > 0 ? '10px 0 0' : '0', borderTop: i > 0 ? '1px solid #1a1a22' : 'none', marginTop: i > 0 ? '10px' : 0 }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: actionDot(action.status), flexShrink: 0, marginTop: '5px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '12.5px', marginBottom: '3px', color: '#eeeef5' }}>{action.title}</div>
                <div style={{ fontSize: '11px', color: '#52525f', lineHeight: 1.6, marginBottom: '6px' }}>{action.description}</div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' as const }}>
                  {actionBadge(action.status)}
                  <span style={{ fontSize: '10.5px', color: '#52525f' }}>· {fmtTime(action.timestamp)}</span>
                  {action.app_name && <><span style={{ fontSize: '10.5px', color: '#52525f' }}>·</span><Mono color="#52525f">{action.app_name}</Mono></>}
                  {action.cost_delta != null && action.cost_delta < 0 && (
                    <span style={{ fontSize: '10.5px', color: '#22c55e', fontWeight: 600 }}>💰 saves ${Math.abs(action.cost_delta)}/mo</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};